import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Target, Shield, Users, CheckCircle, Info, ArrowRight, Brain } from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { LanguageToggle } from '../components/ui/LanguageToggle'
import { ThemeToggle } from '../components/ui/ThemeToggle'
import { useLanguage } from '../contexts/LanguageContext'
import { supabase } from '../lib/supabase'
// SimpleTracking moved to DashboardPage

export default function BetaPage() {
  const { t } = useLanguage()
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setIsAuthenticated(!!user)
    }
    checkAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsAuthenticated(!!session?.user)
    })

    return () => subscription.unsubscribe()
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-950">
      {/* Header */}
      <header className="bg-white dark:bg-night-900/85 shadow-sm border-b border-gray-100 dark:border-night-850/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                <Brain className="text-white w-5 h-5" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">Muay-Thai.app Beta</h1>
                <p className="text-sm text-gray-500 dark:text-slate-400">powered by TrainIQ</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-gray-900 dark:text-slate-100">Beta</h1>
              </div>
            </a>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              <LanguageToggle />
              {!isAuthenticated && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <Button
                    onClick={() => window.location.href = '/register'}
                    className="bg-white text-purple-600 hover:bg-gray-100 dark:bg-night-850 dark:text-slate-100 dark:hover:bg-night-800 border border-purple-200 dark:border-night-750/60 px-3 sm:px-4 py-2 text-sm sm:text-base shadow-lg shadow-primary-500/10 dark:shadow-none"
                  >
                    {t('register.submit')}
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/login'}
                    className="bg-purple-500 hover:bg-purple-600 text-white dark:bg-ocean-400 dark:hover:bg-ocean-300 px-3 sm:px-4 py-2 text-sm sm:text-base shadow-lg shadow-primary-500/20 dark:shadow-none"
                  >
                    {t('nav.login')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">

        {/* Redirect authenticated users to dashboard */}
        {isAuthenticated && (
          <div className="mb-8 max-w-2xl mx-auto">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg p-4 text-center">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
                ✅ Du bist bereits eingeloggt!
              </h3>
              <p className="text-green-700 dark:text-green-300 mb-4">
                Gehe zu deinem Training Dashboard um deine Sessions zu verwalten.
              </p>
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                🎯 Zum Training Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Beta Information Section - only show if not authenticated */}
        {!isAuthenticated && (
          <>
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <div className="rounded-3xl bg-white/95 dark:bg-night-850/70 border border-gray-100 dark:border-night-800/60 shadow-xl shadow-primary-500/5 dark:shadow-none px-6 sm:px-10 lg:px-14 py-12">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center px-4 py-2 bg-purple-100 dark:bg-ocean-500/20 text-purple-800 dark:text-slate-100 rounded-full text-sm font-medium mb-4">
                    <Target className="w-4 h-4 mr-2" />
                    {t('beta.badge')}
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-slate-100 mb-4">
                    {t('beta.title')}
                  </h2>
                  <p className="text-lg sm:text-xl text-gray-600 dark:text-slate-200 max-w-3xl mx-auto">
                    {t('beta.subtitle')}
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={() => window.location.href = '/register'}
                      className="bg-purple-500 hover:bg-purple-600 text-white dark:bg-ocean-400 dark:hover:bg-ocean-300 px-6 py-3 text-base shadow-lg shadow-primary-500/20 dark:shadow-none"
                    >
                      {t('register.submit')}
                    </Button>
                    <Button
                      onClick={() => window.location.href = '/login'}
                      className="bg-white text-purple-600 hover:bg-gray-100 dark:bg-night-850 dark:text-slate-100 dark:hover:bg-night-800 border border-purple-200 dark:border-night-750/60 px-6 py-3 text-base shadow-lg shadow-primary-500/10 dark:shadow-none"
                    >
                      {t('nav.login')}
                    </Button>
                  </div>
                </div>

                {/* Features Grid */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6">
                <Target className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">{t('beta.feature1.title')}</h3>
                <p className="text-gray-600 dark:text-slate-200 text-sm">
                  {t('beta.feature1.desc')}
                </p>
              </Card>

              <Card className="p-6">
                <Brain className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">{t('beta.feature2.title')}</h3>
                <p className="text-gray-600 dark:text-slate-200 text-sm">
                  {t('beta.feature2.desc')}
                </p>
              </Card>

              <Card className="p-6">
                <Users className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">{t('beta.feature3.title')}</h3>
                <p className="text-gray-600 dark:text-slate-200 text-sm">
                  {t('beta.feature3.desc')}
                </p>
              </Card>
            </div>
          </div>
        </motion.section>

        {/* Moderation & Privacy Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <Card className="p-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-night-825/80 dark:via-night-800/65 dark:to-night-850/70 border-blue-200 dark:border-night-750/40">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-3">
                  {t('beta.privacy.title')}
                </h3>
                <div className="space-y-3 text-gray-700 dark:text-slate-200">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <p>
                      <strong>Manuelle Freigabe:</strong> Jede Registrierung wird manuell geprüft und freigeschaltet.
                      Du erhältst eine Bestätigung, sobald dein Zugang aktiviert wurde.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <p>
                      <strong>Datenschutz:</strong> Alle Trainingsdaten werden verschlüsselt gespeichert und
                      ausschließlich für die KI-Entwicklung verwendet. Keine Weitergabe an Dritte.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <p>
                      <strong>Anonymisierung:</strong> Persönliche Daten werden von Trainingsdaten getrennt und
                      für die KI-Analyse anonymisiert verarbeitet.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <p>
                      <strong>Löschung:</strong> Du kannst jederzeit die Löschung deiner Daten beantragen.
                      Kontakt: beta@trainiq.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.section>

        {/* How it Works */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="rounded-3xl bg-white/95 dark:bg-night-850/70 border border-gray-100 dark:border-night-800/60 shadow-xl shadow-primary-500/5 dark:shadow-none px-6 sm:px-10 lg:px-14 py-12">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-slate-100 text-center mb-8">
              So funktioniert es
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  step: '1',
                  title: 'Registrieren',
                  description: 'Erstelle dein Beta-Profil mit Erfahrungslevel und Trainingszielen.'
                },
                {
                  step: '2',
                  title: 'Freischaltung',
                  description: 'Warte auf die manuelle Freigabe durch unser Team (meist binnen 24h).'
                },
                {
                  step: '3',
                  title: 'Training tracken',
                  description: 'Erfasse deine Trainings mit Dauer, Inhalt und persönlichen Notizen.'
                },
                {
                  step: '4',
                  title: 'KI entwickeln',
                  description: 'Deine Daten helfen uns, intelligente Trainingspläne zu entwickeln.'
                }
              ].map((item, index) => (
                <Card key={index} className="p-6 text-center">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-night-850 text-purple-600 dark:text-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                    {item.step}
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">{item.title}</h4>
                  <p className="text-gray-600 dark:text-slate-200 text-sm">{item.description}</p>
                  {index < 3 && (
                    <ArrowRight className="w-4 h-4 text-gray-300 dark:text-slate-500 mx-auto mt-4 hidden md:block" />
                  )}
                </Card>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Clear Instructions */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-8"
        >
          <Card className="p-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-night-800 dark:to-night-850 border-blue-200 dark:border-ocean-500/30">
            <div className="text-center">
              <Info className="w-12 h-12 text-blue-600 dark:text-ocean-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-4">
                Jetzt loslegen! 🥊
              </h3>
              <div className="text-left max-w-2xl mx-auto space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500 dark:bg-ocean-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                  <p className="text-gray-700 dark:text-slate-200">
                    <strong className="text-gray-900 dark:text-slate-100">Nach dem Training:</strong> Beschreibe kurz, was du trainiert hast (z.B. "Jab-Cross-Hook Kombinationen, 30min Pad-Work")
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500 dark:bg-ocean-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                  <p className="text-gray-700 dark:text-slate-200">
                    <strong className="text-gray-900 dark:text-slate-100">Dauer angeben:</strong> Wie lange hast du trainiert? (in Minuten)
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500 dark:bg-ocean-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                  <p className="text-gray-700 dark:text-slate-200">
                    <strong className="text-gray-900 dark:text-slate-100">Speichern:</strong> Klicke auf "Training speichern" - fertig!
                  </p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700/30">
                <p className="text-sm text-yellow-800 dark:text-yellow-100">
                  💡 <strong className="text-yellow-900 dark:text-yellow-50">Tipp:</strong> Je detaillierter deine Beschreibung, desto besser kann die KI lernen.
                  Aber auch kurze Einträge helfen!
                </p>
              </div>
            </div>
          </Card>
        </motion.section>

        {/* Beta Dashboard */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-white dark:bg-night-825/95 rounded-lg shadow-sm dark:shadow-lg border border-gray-200 dark:border-night-750/40 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 dark:from-night-750 dark:to-ocean-500 px-6 py-4">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Target className="w-5 h-5" />
                Beta-Zugang aktiviert
              </h3>
              <p className="text-purple-100 dark:text-ocean-100 text-sm mt-1">
                Du kannst jetzt deine Trainings tracken und zur KI-Entwicklung beitragen
              </p>
            </div>
          </div>
            </motion.section>

            {/* Info Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8"
            >
              <Card className="p-4 bg-blue-50 dark:bg-night-825/70 border-blue-200 dark:border-night-750/40">
                <div className="flex items-center gap-3">
                  <Info className="w-5 h-5 text-blue-600" />
                  <p className="text-blue-800 dark:text-slate-200 text-sm">
                    <strong>Hinweis:</strong> Diese Beta-Version dient ausschließlich der Datensammlung
                    für die KI-Entwicklung. Trainingspläne und Analysen sind noch nicht verfügbar.
                  </p>
                </div>
              </Card>
            </motion.div>
          </>
        )}

      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 dark:bg-night-900 text-white dark:text-slate-100 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold">Muay-Thai.app</span>
            </div>
            <p className="text-gray-400 dark:text-ocean-300/80 mb-4">
              {t('footer.tagline')}
            </p>
            <div className="flex flex-col items-center justify-center gap-2 text-sm text-gray-500 dark:text-ocean-200/80">
              <div className="flex items-center gap-4">
                <span>{t('footer.copyright')}</span>
                <span>•</span>
                <span>{t('footer.powered')}</span>
                <span>•</span>
                <span>{t('footer.version')}</span>
              </div>
              <div className="text-xs text-gray-400 dark:text-ocean-300/70">
                {t('footer.joint')}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
